import axios from 'axios'
import { getStoredPreviewAccessKey, PREVIEW_ACCESS_HEADER } from '../../api/client'
import { SAAS_API_BASE_URL_EXPORT } from '../api'

/**
 * Local Public Website client (LiangCollection 1:1 Absorption — "Local
 * Public Website" gate). Deliberately its own axios instance with
 * `withCredentials: false` — this surface must work for a signed-out
 * visitor exactly as well as a signed-in admin, so it must never depend
 * on the SaaS session cookie the way `../api.ts`'s `saasApi` does.
 * Points at the backend's `app/api/routes/public_site.py` router, which
 * likewise takes no `require_workspace_member()` dependency.
 */
const PUBLIC_API_BASE_URL = SAAS_API_BASE_URL_EXPORT.replace(/\/api\/saas$/, '/api/public/sites')

export const publicApi = axios.create({
  baseURL: PUBLIC_API_BASE_URL,
  withCredentials: false,
  timeout: 30000,
})

// LOCAL_PREVIEW_GAP_AUDIT (2026-08-21) — this signed-out public surface
// must stay reachable "with zero auth/session" (see the module docstring
// above) in every environment, but the Online Preview whole-origin gate
// (`PREVIEW_ACCESS_PASSWORD`, unset on Local — see
// `backend/app/core/config.py` / `PreviewAccessGate.tsx`) fails every
// request closed *before* `public_site.py`'s own no-auth-required routing
// is ever reached, on any environment where that gate is on. `../api.ts`'s
// `saasApi` and `../../api/client.ts`'s `api` already attach this same
// header; this third, deliberately-credential-less axios instance was the
// one call site that didn't, which is why the real (signed-out) public
// LiangCollection site 401'd on Preview while working fine on Local
// (where the gate is off) — a genuine Local/Preview behavioral gap, not a
// business-logic difference, so it belongs here rather than in any new
// per-environment branch.
publicApi.interceptors.request.use((config) => {
  const key = getStoredPreviewAccessKey()
  if (key) {
    config.headers = config.headers ?? {}
    ;(config.headers as Record<string, string>)[PREVIEW_ACCESS_HEADER] = key
  }
  return config
})

export interface PublicApiError extends Error {
  status?: number
}

// ITEM-DETAIL-IMAGE-P0 (2026-08) — `public_site.py`'s `_asset_url()` always
// returns a *server-relative* path (`/api/public/sites/{ws}/{site}/assets/
// {id}/{role}`), the correct shape for a same-origin deployment (Local,
// where the frontend dev server proxies `/api` to the backend). On a
// deployment where the frontend and API are on two different origins —
// exactly Online Preview's Cloudflare Pages (`app-preview.vistoera.com`)
// + Render (`api-preview.vistoera.com`) split, and Production will be the
// same shape — that frontend origin never proxies `/api/*` at all (it is a
// static SPA host; an unmatched `/api/...` path there falls straight
// through to `index.html`, HTTP 200 `text/html`). Every `<img src>` built
// directly from a raw `cover_url`/`gallery[].thumbnail_url`/etc. therefore
// silently resolved against the *page's* origin instead of the API's,
// which is exactly why the hero viewer's Deep Zoom tiles rendered (they
// already go through `resolvePublicApiAbsoluteUrl` below, added
// specifically for this reason) while every plain gallery thumbnail and
// the item-preview/cover thumbnail did not (they used the raw relative
// string as-is). Rewriting every such path to an absolute API-origin URL
// **once, here, for every public API response** — rather than adding a
// second call site's worth of `resolvePublicApiAbsoluteUrl(...)` calls
// per `<img>` — is the "one shared resolver" fix: every current and
// future public asset URL field (`cover_url`, `cover_original_url`,
// `cover_thumbnail_url`, `gallery[].url/thumbnail_url/original_url`,
// `hero_image_url`, Graphic Page `asset_url`/`asset_original_url`/nested
// `cover_url`s, …) gets the exact same treatment automatically, with zero
// per-field/per-page follow-up work.
const PUBLIC_ASSET_PATH_RE = /^\/api\/public\/sites\//

function rewritePublicAssetUrls<T>(value: T): T {
  if (typeof value === 'string') {
    return (PUBLIC_ASSET_PATH_RE.test(value) ? resolvePublicApiAbsoluteUrl(value) : value) as unknown as T
  }
  if (Array.isArray(value)) {
    return value.map((v) => rewritePublicAssetUrls(v)) as unknown as T
  }
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = rewritePublicAssetUrls(v)
    }
    return out as T
  }
  return value
}

publicApi.interceptors.response.use(
  (r) => {
    r.data = rewritePublicAssetUrls(r.data)
    return r
  },
  (err) => {
    const detail = err.response?.data?.detail
    const message = typeof detail === 'string' ? detail : err.message
    const wrapped = new Error(message) as PublicApiError
    wrapped.status = err.response?.status
    return Promise.reject(wrapped)
  },
)

export interface PublicItemSummary {
  slug: string
  label: string
  secondary_label: string | null
  accession_number: string
  creator: string
  era: string
  cover_url: string | null
  cover_thumbnail_url: string | null
  category: { id: string; name: string; slug: string } | null
}

export interface PublicGalleryImage {
  asset_id: string
  caption: string
  image_type: string
  url: string | null
  thumbnail_url: string | null
  original_url: string | null
}

export interface PublicTermRef {
  id: string
  name: string
  slug: string
}

export interface PublicDimension {
  label?: string
  height?: number
  width?: number
  depth?: number
  diameter?: number
  unit?: string
  notes?: string
}

export interface PublicProvenanceEntry {
  date_display?: string
  owner_or_source?: string
  acquisition_method?: string
  location?: string
  notes?: string
}

export interface PublicPublicationEntry {
  title?: string
  author?: string
  publisher?: string
  publication_year?: number | string
  page_reference?: string
  notes?: string
}

export interface PublicExhibitionEntry {
  title?: string
  institution?: string
  city?: string
  start_date?: string
  end_date?: string
  notes?: string
}

export interface PublicItemDetail extends Omit<PublicItemSummary, 'category'> {
  /** Only meaningful when this response was returned via a
   * `preview_token` bypass (see `fetchPublicItem`) ? a real anonymous
   * visitor's response is always `'published'`. */
  status?: string
  culture_or_dynasty: string
  material: string
  dimension_summary: string
  description: string
  research_notes: string
  inscription: string
  seal: string
  dimensions: PublicDimension[]
  provenance: PublicProvenanceEntry[]
  publications: PublicPublicationEntry[]
  exhibition_history: PublicExhibitionEntry[]
  cover_asset_id: string | null
  cover_url: string | null
  cover_original_url: string | null
  gallery: PublicGalleryImage[]
  categories: PublicTermRef[]
  topics: PublicTermRef[]
  updated_at: string | null
  /** ITEM-DETAIL-INITIAL-IMAGE-RACE-P0: true only for a genuinely
   * short window right after create/edit where a browser-side image
   * upload for this item is still in flight and not yet attached
   * (`cover_asset_id`/`gallery` are still empty as a result). Never
   * true for an item that simply has no image at all — see
   * `_image_pending_hint` in `public_site.py`. */
  image_pending?: boolean
}

/** Mirrors `AssetDeepZoomDerivative` in `../api.ts` — same storage-neutral
 * shape, just returned by the unauthenticated public counterpart route. */
export interface PublicDeepZoomDerivative {
  status: 'not_started' | 'pending' | 'processing' | 'ready' | 'failed'
  error_message: string | null
  width?: number | null
  height?: number | null
  tile_size?: number | null
  overlap?: number | null
  format?: string | null
  levels?: number | null
  processor_version?: string | null
  descriptor_url?: string
  tiles_base_url?: string
}

export interface PublicTermSummary {
  slug: string
  name: string
  name_secondary: string | null
  description: string
  cover_url: string | null
  item_count: number
}

export interface PublicPublicationSummary {
  slug: string
  label: string
  secondary_label: string | null
  description: string
  venue: string
  start_date: string
  end_date: string
  author: string
  cover_url: string | null
}

export interface PublicPublicationDetail extends PublicPublicationSummary {
  description_long: string
  related_items: PublicItemSummary[]
}

export interface PublicHome {
  site: { name: string; slug: string; vocabulary: Record<string, { singular?: string; plural?: string }> }
  hero_text: string
  hero_image_url: string | null
  featured_items: PublicItemSummary[]
  featured_exhibitions: PublicPublicationSummary[]
  featured_research: PublicPublicationSummary[]
  item_count: number
}

const p = (workspaceSlug: string, siteSlug: string, path: string) => `/${workspaceSlug}/${siteSlug}${path}`

export const fetchPublicHome = (workspaceSlug: string, siteSlug: string) =>
  publicApi.get<PublicHome>(p(workspaceSlug, siteSlug, '/home'))

export const fetchPublicItems = (
  workspaceSlug: string,
  siteSlug: string,
  params: { category?: string; topic?: string; page?: number; page_size?: number },
) => publicApi.get<{ items: PublicItemSummary[]; total: number; page: number; page_size: number }>(
  p(workspaceSlug, siteSlug, '/items'),
  { params },
)

export const fetchPublicItem = (workspaceSlug: string, siteSlug: string, slug: string, previewToken?: string | null) =>
  publicApi.get<PublicItemDetail>(p(workspaceSlug, siteSlug, `/items/${slug}`), {
    params: previewToken ? { preview_token: previewToken } : undefined,
  })

export const fetchPublicAssetDeepZoomDerivative = (workspaceSlug: string, siteSlug: string, assetId: string) =>
  publicApi.get<PublicDeepZoomDerivative>(p(workspaceSlug, siteSlug, `/assets/${assetId}/derivatives/deepzoom`))

/** `descriptor_url`/`tiles_base_url`/every other public asset URL field
 * above are server-relative paths (`/api/public/sites/...`) — this
 * resolves them against the API origin (same helper pattern as the admin
 * `DeepZoomViewer`), never against `publicApi`'s own baseURL, since
 * those two paths live under different prefixes. Idempotent by design:
 * the response interceptor above already calls this for every matching
 * field, so a caller that (redundantly, but harmlessly) calls it again
 * on an already-absolute URL gets that same URL straight back rather
 * than a doubled-up origin. */
export const resolvePublicApiAbsoluteUrl = (path: string) => {
  if (/^https?:\/\//.test(path)) return path
  const origin = SAAS_API_BASE_URL_EXPORT.replace(/\/api\/saas$/, '')
  return `${origin}${path}`
}

export const fetchPublicCategories = (workspaceSlug: string, siteSlug: string) =>
  publicApi.get<{ items: PublicTermSummary[] }>(p(workspaceSlug, siteSlug, '/categories'))

export const fetchPublicCategory = (workspaceSlug: string, siteSlug: string, slug: string) =>
  publicApi.get<{ term: { slug: string; name: string; description: string }; items: PublicItemSummary[] }>(
    p(workspaceSlug, siteSlug, `/categories/${slug}`),
  )

export const fetchPublicTopics = (workspaceSlug: string, siteSlug: string) =>
  publicApi.get<{ items: PublicTermSummary[] }>(p(workspaceSlug, siteSlug, '/topics'))

export const fetchPublicTopic = (workspaceSlug: string, siteSlug: string, slug: string) =>
  publicApi.get<{ term: { slug: string; name: string; description: string }; items: PublicItemSummary[] }>(
    p(workspaceSlug, siteSlug, `/topics/${slug}`),
  )

export const fetchPublicExhibitions = (workspaceSlug: string, siteSlug: string) =>
  publicApi.get<{ items: PublicPublicationSummary[] }>(p(workspaceSlug, siteSlug, '/exhibitions'))

export const fetchPublicExhibition = (workspaceSlug: string, siteSlug: string, slug: string) =>
  publicApi.get<PublicPublicationDetail>(p(workspaceSlug, siteSlug, `/exhibitions/${slug}`))

export const fetchPublicResearchList = (workspaceSlug: string, siteSlug: string) =>
  publicApi.get<{ items: PublicPublicationSummary[] }>(p(workspaceSlug, siteSlug, '/research'))

export const fetchPublicResearch = (workspaceSlug: string, siteSlug: string, slug: string) =>
  publicApi.get<PublicPublicationDetail>(p(workspaceSlug, siteSlug, `/research/${slug}`))

// -------------------------------------------------------- Graphic Pages (图文编辑)
// Snapshot-only public read — see backend `public_site.py`'s
// `list_graphic_pages`/`get_graphic_page`, which read exclusively from
// `GraphicPage.published_snapshot_json` (never live Module/Block rows).
import type { GraphicPage, GraphicPageTree } from '../graphicPageTypes'

export const fetchPublicGraphicPages = (workspaceSlug: string, siteSlug: string) =>
  publicApi.get<{ items: GraphicPage[] }>(p(workspaceSlug, siteSlug, '/graphic-pages'))

export const fetchPublicGraphicPage = (workspaceSlug: string, siteSlug: string, slug: string) =>
  publicApi.get<GraphicPageTree>(p(workspaceSlug, siteSlug, `/graphic-pages/${slug}`))
